import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { intersectionBy } from 'lodash';
import { Observable } from 'rxjs';
import { CartOffer, CoverLogoPosition, Offer, OfferCapacity, OfferOption, Product, ProviderDatacenterCompanyPool, ProviderResourceCompanyPool, Rental } from 'src/app/models/verylys';
import { AbstractService, PaginationResult, Populate } from '../abstract/abstract.service';
import { FileMediaService } from '../file-media/file-media.service';
import { OfferService } from '../offer/offer.service';
import { ProductService } from '../product/product.service';
import { VlApiService } from '../vl-api/vl-api.service';
import { FileMedias, PopulateFileMedia } from 'src/app/models/verylys/verylys';
import { SocketIOService } from '../socketio/socketio.service';
import { CompanyPeriodicity } from 'src/app/models/verylys/companyPeriodicity.model';

type CalculatedPriceItem = { amount: number, format: string };

export type CartOfferCalculatedPrice = {
    total: CalculatedPriceItem,
    unit: CalculatedPriceItem,
    unit_no_promotion: CalculatedPriceItem,
    total_no_promotion: CalculatedPriceItem,
    promotions: { name: string, description: string, amount: string }[]
}

export interface CartOfferOfferRepresentation {
    options: {
        _id: string;
        name: string;
        icon: string | SafeResourceUrl;
        value: any;
        unit: string;
    }[];

    capacities: {
        _id: string;
        name: string;
        icon: string | SafeResourceUrl;
        value: any;
        unit: string;
    }[];

    providerDatacenterCompanyPool: {
        _id: string;
        name: string;
        icon: string | SafeResourceUrl;
        outOfStock: boolean;
    };
}

export interface CartOfferProductRepresentation {
    offer: string;
    offerShort: string;
    product: string;
    productShort: string;
    productImage: string | SafeResourceUrl;
    productImageNoLogo: string | SafeResourceUrl;
    productIcon: string | SafeResourceUrl;
    productLogo: string | SafeResourceUrl;
    offerLogo: string | SafeResourceUrl;
    coverLogoPosition: CoverLogoPosition;
}

export interface CartOfferRepresentation {
    offer: CartOfferOfferRepresentation;
    product: CartOfferProductRepresentation;
    preorder: boolean;
    preorder_delivery_at: Date | string;
}

@Injectable({
    providedIn: 'root'
})
export class CartOfferService extends AbstractService<CartOffer> {
    route = "/cart-offers/";
    resourceReferenceLabel = "CartOffer";

    protected crudMethods = {
        create: false,
        update: false,
        delete: false,
        get: false,
        getAll: false
    }

    constructor(protected vlApiService: VlApiService,
        private productService: ProductService,
        private socketIoService: SocketIOService) {
        super(vlApiService, socketIoService);
    }

    /**
     * Remove from the cart
     */
    public removeFromCart(cartOffer: CartOffer): Observable<void> {
        return this.vlapi.delete<void>(`${this.route}${cartOffer._id}/remove-from-cart`);
    }

    /**
     * Change the datacenter company pool of a cart offer
     * @param cartOffer                    CartOffer
     * @param providerDatacenterCompanyPool    providerDatacenterCompanyPool
    */
    public changeDatacenter(cartOffer: CartOffer, providerDatacenterCompanyPool: ProviderDatacenterCompanyPool): Observable<void> {
        return this.vlapi.post<any>(`${this.route}${cartOffer._id}/datacenter`, { value: providerDatacenterCompanyPool._id });
    }

    /**
     * Change the periodicity 
     */
    public changePeriodicity(cartOffer: CartOffer, offerPeriodicity: CompanyPeriodicity): Observable<void> {
        return this.vlapi.post<any>(`${this.route}${cartOffer._id}/change-periodicity`, { companyPeriodicityId: offerPeriodicity._id });
    }

    /** 
     * Get the price of a cart offer
    */
    public calculatedPrice(cartOffer: CartOffer): Observable<CartOfferCalculatedPrice> {
        return this.vlapi.get<CartOfferCalculatedPrice>(`${this.route}${cartOffer._id}/calculated-price`);
    }

    /**
     * Validate
     * 
     * @param cartOffer       CartOffer
    */
    public checkout(cartOffer: CartOffer) {
        return this.vlapi.post<CartOffer>(`${this.route}${cartOffer._id}/checkout`);
    }

    /**
    * Get the cart offer representation 
    * 
    * @param cartOffer           Cart offer
    * @param populatedProducts   The products populated (offers offers.capacities.capacity_type offers.quantity offers.options offers.provider_resource_company_pool.provider_datacenter_company_pool)
    *                            If not set, the product will be fetched
    *                            We are using an array of product because it's more simple when we need to use this 
    */
    public async getCartOfferRepresentation(cartOffer: CartOffer, config: { image?: boolean }, populatedProducts?: Product[]): Promise<CartOfferRepresentation> {
        if (!cartOffer) {
            return;
        }

        const subRepresentation = (value: any, array: any[], key: string) => {
            const find = array.find((item: any) => item._id == value[key]);

            return {
                name: find?.name,
                value: value.parameters?.choice_value?.value,
                icon: find?.parameters?.icon?.value,
                _id: value._id,
                unit: find.service_capacity?.capacity_type?.parameters?.unit?.value
            }
        }

        if (!populatedProducts) {
            const productResult: PaginationResult<Product> = await this.productService.getDetailedList().toPromise()

            populatedProducts = productResult?.results;
        }

        const representation: CartOfferRepresentation = {} as any;
        const products: Product[] = populatedProducts;

        if (!products?.length) {
            return;
        }

        for (let product of products) {
            const offers: Offer[] = product.offers as Offer[];

            if (!offers?.length) {
                continue;
            }

            for (let offer of offers) {
                if (offer._id != cartOffer?.offer && offer._id != (cartOffer.offer as Offer)?._id) {
                    continue;
                }

                const productPreordering: boolean = product.parameters?.preorder?.value;
                if (productPreordering) {
                    representation.preorder = true;
                    representation.preorder_delivery_at = product?.parameters?.preorder_delivery_at?.value;
                }

                representation.product = {
                    offer: offer.name,
                    offerShort: offer.parameters.shortname.value,
                    product: product.name,
                    productShort: product.parameters.shortname.value,
                    productImage: product.file_medias?.game_cover?.MAIN,
                    productImageNoLogo: product.file_medias?.game_cover?.NO_LOGO,
                    productIcon: product.file_medias?.icon?.TABLE,
                    productLogo: product.file_medias?.logo?.MAIN,
                    offerLogo: offer.file_medias?.logo?.MAIN,
                    coverLogoPosition: product.parameters?.cover_logo_position?.value
                }

                representation.offer = {
                    options: [],
                    capacities: []
                } as any;

                const options: OfferOption[] = offer.options as OfferOption[];
                const capacities: OfferCapacity[] = offer.capacities as OfferCapacity[];

                const providerResourceCompanyPool: ProviderResourceCompanyPool = offer.provider_resource_company_pool as ProviderResourceCompanyPool;
                const providerDatacenterCompanyPools: ProviderDatacenterCompanyPool[] = providerResourceCompanyPool?.provider_datacenter_company_pools as ProviderDatacenterCompanyPool[];

                // Get the options 
                if (cartOffer.options?.length) {
                    for (const option of cartOffer.options) {
                        if (!option.parameters?.is_active?.value) {
                            continue;
                        }

                        const optionRepresentation = subRepresentation(option, options, 'offer_option');

                        if (optionRepresentation) {
                            representation.offer.options.push(optionRepresentation)
                        }
                    }
                }

                // Get the capacities 
                if (cartOffer.capacities?.length) {
                    for (const capacity of cartOffer.capacities) {
                        const capacityRepresentation = subRepresentation(capacity, capacities, 'offer_capacity');

                        if (capacityRepresentation) {
                            representation.offer.capacities.push(capacityRepresentation)
                        }
                    }
                }

                // Get the datacenter 
                const findProviderDatacenterCompanyPool: ProviderDatacenterCompanyPool = providerDatacenterCompanyPools?.find((value) => value._id == cartOffer.provider_datacenter_company_pool);

                if (findProviderDatacenterCompanyPool) {
                    representation.offer.providerDatacenterCompanyPool = {
                        _id: findProviderDatacenterCompanyPool._id,
                        name: findProviderDatacenterCompanyPool.name,
                        icon: 'fa-solid fa-globe',
                        outOfStock: !productPreordering && findProviderDatacenterCompanyPool.parameters?.has_stock_on_configured_periodicity?.value == false ? true : false
                    }
                }
            }
        }

        return representation;
    }

    public validate(value: CartOffer, ...args: any): void {

    }
}
